import React, { useState, useEffect, useContext } from "react";
import * as moment from "moment";
import { toast } from "react-toastify";

import "./ProbationManagement.css";
import Tables from "components/table/table";
import Context from "context/context";
import probationManagement from "apis/panel/probationManagement/probationManagement";

import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";

const ACTIONS = {
  APPROVE: "Approve",
  REJECT: "Reject",
  EXTEND: "Extend",
};

const REJECTION_REASONS = ["Performance Issue", "Sabbatical", "Resigned"];

const ACTION_STYLES = {
  [ACTIONS.APPROVE]: {
    color: "green",
    icon: <CheckCircleOutlineIcon style={{ verticalAlign: "sub" }} />,
  },
  [ACTIONS.REJECT]: {
    color: "red",
    icon: <CancelOutlinedIcon style={{ verticalAlign: "sub" }} />,
  },
  [ACTIONS.EXTEND]: {
    color: "orange",
    icon: <HourglassTopIcon style={{ verticalAlign: "sub" }} />,
  },
};

const probationPeriods = {
  1: "1 Month",
  2: "2 Months",
  3: "3 Months",
  6: "6 Months",
  9: "9 Months",
  12: "12 Months",
};

const LevelName = {
  L1: "Manager (level 1)",
  L2: "Manager (level 2)",
  L3: "Manager (level 3)",
  HR: "HR",
};

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    cursor: "pointer",
    backgroundColor: "#002c5d",
    color: "#ffffff",
  },
});

const ProbationManagement = () => {
  const { state, dispatch } = useContext(Context);
  const [roleCode] = useState(state.role.roleCode);
  const {
    getProbationManagementDataAPI,
    updateProbationManagementDataAPI,
    getProbationManagementDetailsAPI,
  } = probationManagement();
  const [data, setData] = useState([]);
  const [offset, setOffset] = useState(1);
  const [count, setCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [selectedRow, setSelectedRow] = useState({});

  const [action, setAction] = useState(ACTIONS.APPROVE);
  const [reason, setReason] = useState("");
  const [extensionDate, setExtensionDate] = useState(null);
  const [extensionReason, setExtensionReason] = useState("");
  const [extendedProbationPeriod, setExtendedProbationPeriod] = useState();
  const [error, setError] = useState("");
  const [detailsDialog, setDetailsDialog] = React.useState(false);
  const [details, setDetails] = React.useState([]);

  useEffect(() => {
    handlegetProbationManagementDataAPI();
  }, [offset, searchValue]);

  const handlegetProbationManagementDataAPI = async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      let values;
      values = {
        offset: (offset - 1) * 10,
        limit: 10,
      };
      if (searchValue) {
        values = {
          ...values,
          searchValue,
        };
      }
      let res = await getProbationManagementDataAPI(values);
      setData(res?.data?.data || []);
      setCount(res?.data?.count);
      setSerialNumber(offset);
      dispatch({ type: "SET_LOADING", payload: false });
    } catch (err) {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = (obj) => {
    setSelectedRow(obj);
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
    setAction(ACTIONS.APPROVE);
    setReason("");
    setExtensionDate(null);
    setExtensionReason("");
    setExtendedProbationPeriod();
    setError("");
  };

  const handleSubmit = async () => {
    if (action === ACTIONS.REJECT && !reason) {
      setError("Please select a reason for rejection.");
      toast.error("Please select a reason for rejection.", {
        toastId: "Error saving Data",
      });
    } else if (
      action === ACTIONS.EXTEND &&
      (!extensionReason || !extensionDate)
    ) {
      let text = "";
      if (!extensionDate && !extensionReason) {
        text =
          "Please provide an extended end date and a reason for extension.";
      } else if (!extensionDate) {
        text = "Please provide an extended end date.";
      } else if (!extensionReason) {
        text = "Please provide a reason for extension.";
      }
      setError(text);
      toast.error(text, { toastId: "Error saving Data" });
    } else {
      setError("");
    }
    const payload = { action };
    if (action === ACTIONS.REJECT) payload.reason = reason;
    if (action === ACTIONS.EXTEND) {
      payload.reason = extensionReason;
      payload.extensionDate = extensionDate
        ? moment(extensionDate).format("YYYY-MM-DD")
        : "";
      payload.extensionPeriod = parseInt(extendedProbationPeriod);
    }
    payload.action = action;
    payload.employeeId = selectedRow?.employeeId;
    payload.employeeProbationId = selectedRow?.employeeProbationId;
    payload.employeeProbationApprovalId =
      selectedRow?.employeeProbationApprovalId;

    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const response = await updateProbationManagementDataAPI(payload);
      if (response.status === "Ok") {
        toast.success("Updated successfully");
        if (offset === 1) {
          handlegetProbationManagementDataAPI();
        } else {
          setOffset(1);
        }
      }
      dispatch({ type: "SET_LOADING", payload: false });
      onClose();
    } catch (error) {
      dispatch({ type: "SET_LOADING", payload: false });
      toast.error("Failed to update status");
    }
  };

  const handleGetDetails = async (employee) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      let values;
      values = {
        employeeId: employee?.employeeId,
        employeeProbationId: employee?.employeeProbationId,
      };
      let res = await getProbationManagementDetailsAPI(values);
      setDetails(res?.data || {});
      setDetailsDialog(true);
      dispatch({ type: "SET_LOADING", payload: false });
    } catch (err) {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const isEditable = (employee) =>
    (employee.currentStage === "HR" &&
      employee.status === "Pending" &&
      roleCode === "R011") ||
    roleCode !== "R011" ||
    (roleCode === "R011" &&
      state?.employee?.employeeId === employee?.approverId && employee.status === "Pending" && employee?.approvalStatus === "Pending");

  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "5px",
      }}
    >
      <Tables
        className="employeeRosterMediaQuery"
        title={
          <Box className="EmployeeRoster" sx={{ pt: 2, pb: 2, pl: 1 }}>
            Probation Management
          </Box>
        }
        searchBar={roleCode === "R001" || roleCode === "R011"}
        role={roleCode}
        setSearchValue={setSearchValue}
        heading={[
          // "S.No",
          "Employee Id",
          "Employee Name",
          // "Department",
          "Designation",
          "Probation Start Date",
          "Probation End Date",
          "Current Stage",
          "Status",
          "Action",
          "View Details",
        ]}
        data={data?.map((employee, index) => [
          // index + 1 + (serialNumber - 1) * 10,
          employee?.employeeCode,
          employee?.employeeName,
          // employee?.departmentName,
          employee?.designationName,
          employee?.probationStartDate
            ? moment(employee?.probationStartDate).format("DD-MMM-YYYY")
            : "-",
          employee?.probationEndDate
            ? moment(employee?.probationEndDate).format("DD-MMM-YYYY")
            : "-",
          <div className="pm-stage-text">
            {employee?.currentStage ? LevelName[employee?.currentStage] : "-"}
          </div>,
          employee?.status,
          <CustomWidthTooltip
            title={
              isEditable(employee)
                ? "Click to submit your decision regarding the employee’s probation."
                : "Not allowed"
            }
          >
            <div className="pm-view-icon">
              <RemoveRedEyeIcon
                style={{
                  color: isEditable(employee) ? "#E97000" : "grey",
                  cursor: isEditable(employee) ? "pointer" : "not-allowed",
                }}
                onClick={(e) => {
                  if (isEditable(employee)) handleClickOpen(employee);
                }}
                disabled={!isEditable(employee)}
              />
            </div>
          </CustomWidthTooltip>,
          <div className="pm-info-icon">
            <Tooltip title={"Click to view approval Details"} key={index}>
              <InfoIcon
                style={{
                  color: "#E97000",
                  cursor: "pointer",
                }}
                onClick={() => {
                  handleGetDetails(employee);
                  setSelectedRow(employee);
                }}
              />
            </Tooltip>
          </div>,
        ])}
        offset={offset}
        setOffset={setOffset}
        count={count}
        placeholder="Search by ID, Name, Email"
      />

      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ color: ACTION_STYLES[action].color }}>
            {ACTION_STYLES[action].icon}
          </Box>
          <Typography sx={{ color: ACTION_STYLES[action].color }}>
            {action} Employee
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <FormControl fullWidth margin="normal">
            <InputLabel>Action</InputLabel>
            <Select
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setReason("");
                setExtensionDate(null);
                setExtensionReason("");
                setExtendedProbationPeriod();
                setError("");
              }}
              label="Action"
            >
              {Object.values(ACTIONS).map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {action === ACTIONS.REJECT && (
            <FormControl
              fullWidth
              margin="normal"
              error={!reason && error !== ""}
            >
              <InputLabel>Reason</InputLabel>
              <Select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                label="Reason"
              >
                {REJECTION_REASONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {action === ACTIONS.EXTEND && (
            <>
              <FormControl
                fullWidth
                variant="outlined"
                margin="normal"
                required
                error={!extendedProbationPeriod && error !== ""}
              >
                <InputLabel id="extended-probation-month-label" required>
                  Extended Probation Month
                </InputLabel>
                <Select
                  labelId="extended-probation-month-label"
                  id="extended-probation-month"
                  value={extendedProbationPeriod || ""}
                  onChange={(e) => {
                    setExtendedProbationPeriod(e.target.value);
                    const date = moment(selectedRow?.probationEndDate)
                      .add(parseInt(e.target.value), "months")
                      .toDate();
                    setExtensionDate(date);
                  }}
                  label="Extended Probation Month"
                  error={!extendedProbationPeriod && error !== ""}
                  helperText={
                    !extendedProbationPeriod && error !== "" ? "Required" : ""
                  }
                >
                  {Object.keys(probationPeriods).map((key) => (
                    <MenuItem key={key} value={key}>
                      {probationPeriods[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Extended Probation End Date"
                  value={extensionDate ?? null}
                  sx={{ marginTop: "16px", width: "100%" }}
                  disabled={true}
                  format="dd/MM/yyyy"
                  renderInput={(params) => (
                    <TextField {...params} helperText={null} />
                  )}
                />
              </LocalizationProvider>
              <TextField
                fullWidth
                margin="normal"
                label="Reason for Probation Extension"
                value={extensionReason}
                onChange={(e) => setExtensionReason(e.target.value)}
                error={!extensionReason && error !== ""}
              />
            </>
          )}
        </DialogContent>

        <div className="pb-mng-dialog-bottom">
          <Button
            onClick={onClose}
            className="pb-mng-dialog-btn"
            sx={{
              backgroundColor: "#757575",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#757575",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            className="pb-mng-dialog-btn"
            onClick={handleSubmit}
            sx={{
              backgroundColor: ACTION_STYLES[action].color,
              color: "#fff",
              "&:hover": {
                backgroundColor: ACTION_STYLES[action].color,
              },
            }}
          >
            Submit
          </Button>
        </div>
      </Dialog>

      <Dialog open={detailsDialog} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            fontSize: "18px",
            fontWeight: "bold",
            textAlign: "center",
            color: "#4a55cbff",
          }}
        >
          Employee Probation Progress Details
          <IconButton
            aria-label="close"
            onClick={() => {
              setDetailsDialog(false);
              setSelectedRow({});
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#666",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <strong>Employee Name:</strong> {selectedRow?.employeeName}
            </div>
            <div>
              <strong>Probation Start Date:</strong>{" "}
              {selectedRow?.probationStartDate
                ? moment(selectedRow?.probationStartDate).format("DD-MMM-YYYY")
                : "-"}
            </div>

            <div>
              <strong>Probation End Date:</strong>{" "}
              {selectedRow?.probationEndDate
                ? moment(selectedRow?.probationEndDate).format("DD-MMM-YYYY")
                : "-"}
            </div>
          </div>
          <br />
          <TableContainer component={Paper}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "15%", minWidth: "120px" }}>
                    <Typography sx={{ fontWeight: "bold", color: "#646aadff" }}>
                      Approval Level
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: "15%", minWidth: "120px" }}>
                    <Typography sx={{ fontWeight: "bold", color: "#646aadff" }}>
                      Approver
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: "15%", minWidth: "120px" }}>
                    <Typography sx={{ fontWeight: "bold", color: "#646aadff" }}>
                      Action
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: "15%", minWidth: "120px" }}>
                    <Typography sx={{ fontWeight: "bold", color: "#646aadff" }}>
                      Acted On
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: "15%", minWidth: "120px" }}>
                    <Typography sx={{ fontWeight: "bold", color: "#646aadff" }}>
                      Reason/Remarks
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: "15%", minWidth: "120px" }}>
                    <Typography sx={{ fontWeight: "bold", color: "#646aadff" }}>
                      Extended Date
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {details && details?.map((obj) => {
                  return (
                    <TableRow key={obj?.approvalLevel}>
                      <TableCell>{obj?.approvalLevel}</TableCell>
                      <TableCell>{obj?.approver}</TableCell>
                      <TableCell>{obj?.action}</TableCell>
                      <TableCell>
                        {obj?.actedOn
                          ? moment(obj?.actedOn).format("DD-MMM-YYYY hh:mm A")
                          : "-"}
                      </TableCell>
                      <TableCell>{obj?.reason}</TableCell>
                      <TableCell>{obj?.extendedDate}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProbationManagement;
